import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { KEYWORD_PINS_TYPE, type KeywordPinView, type WritePinBody } from '../pins/serverTypes';
import { queryKeys } from './queryKeys';

/**
 * Create a keyword pin via `POST user/coffee/customizations/keywordPins`
 * (Real-Magic/Levitate-API#11465). Covers both a new custom pin and the first
 * override of a built-in (a disable toggle or content edit) — the body's
 * `builtin`/`userEdited` flags distinguish them; the server assigns id + position.
 *
 * On success the `keywordPins` query is invalidated so the refetch reflects the
 * new row. `onError` toasts (mutations have no `MutationCache` — see
 * packages/main/CLAUDE.md).
 */
export function useCreateKeywordPin(): UseMutationResult<KeywordPinView, Error, WritePinBody> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<KeywordPinView, Error, WritePinBody>({
		mutationFn: async body => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<KeywordPinView>(
				ImpersonationBroker.composeApiUrl({ urlPath: `user/coffee/customizations/${KEYWORD_PINS_TYPE}` }),
				'POST',
				body
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.keywordPins() });
		},
		onError: () => {
			toast.error('Failed to save keyword pin');
		},
	});
}
